import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { FACEBOOK_CONFIG_ID, INSTAGRAM_CONFIG_ID } from "@polifonia/env";
import { fakeT } from "@polifonia/utils/react/fakeT";
import useSwitch from "@react-hook/switch";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { useFacebookLogin } from "@/pages/channels/hooks/useFacebookLogin";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { FailedConnectModal } from "@/pages/channels/pages/detail/components/FailedConnectModal";
import { isEditableMetaChannelType } from "@/resources/channel/isEditableMetaChannelType";

type ChannelType = Extract<InterludeTypes["Channel"]["type"], "fb" | "ig">;

const t = fakeT;
const translationKeyMap = {
  fb: {
    success: t("page.channel.fb.reauthorize.toast.success"),
  },
  ig: {
    success: t("page.channel.ig.reauthorize.toast.success"),
  },
} satisfies Record<
  ChannelType,
  {
    success: ReturnType<typeof t>;
  }
>;

const configIdMap: Record<ChannelType, string> = {
  ig: INSTAGRAM_CONFIG_ID,
  fb: FACEBOOK_CONFIG_ID,
};

/**
 * This button is only for Facebook and Instagram channels
 * and is used to reauthorize the channel.
 */
const ReauthorizeButton: React.FC<Button.Props> = (props) => {
  const { t } = useTranslation();
  const { channel } = ChannelQueriesContext.useData();
  const { enqueueSnackbar } = useSnackbar();
  const [openFailedConnectModal, toggleFailedConnectModal] = useSwitch();
  const reauthorizeMutation = interlude.channel.useReauthorizeChannel(
    {
      params: {
        channelId: channel.id,
      },
    },
    {
      onSuccess() {
        if (!isEditableMetaChannelType(channel)) return;
        enqueueSnackbar(t(translationKeyMap[channel.type].success), {
          variant: "success",
        });
      },
      onError() {
        toggleFailedConnectModal.on();
      },
    },
  );

  const loginFacebookMutation = useFacebookLogin({
    onSuccess: async ({ authResponse }) => {
      reauthorizeMutation.mutate({
        type: "ig",
        extra: {
          signupAuthCode: authResponse.code,
        },
      });
    },
    onError: () => {
      toggleFailedConnectModal.on();
    },
  });

  const handleReauthorize = useHandler(() => {
    if (!isEditableMetaChannelType(channel)) return;

    loginFacebookMutation.startLogin({
      config_id: configIdMap[channel.type],
    });
  });

  if (!isEditableMetaChannelType(channel)) {
    return null;
  }

  return (
    <>
      <Button
        {...props}
        onClick={handleReauthorize}
        loading={
          loginFacebookMutation.isPending || reauthorizeMutation.isPending
        }
      />
      <FailedConnectModal
        channelType={channel.type}
        open={openFailedConnectModal}
        onClose={toggleFailedConnectModal.off}
        onConfirm={toggleFailedConnectModal.off}
      />
    </>
  );
};

export { ReauthorizeButton };
